import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarX2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

const BadDatesTable = ({ rows, testPrefix }) => (
  <div className="max-h-[60vh] overflow-auto">
    <table className="w-full text-xs" data-testid={`${testPrefix}-bad-dates-table`}>
      <thead className="sticky top-0 bg-white">
        <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
          {["Document", "Type", "Party", "Posting date", "Document date", "Status", "Amount", "Why flagged"].map((h) => <th key={h} className={`py-2 pr-3 ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.doc}-${r.item}-${i}`} className="border-b border-slate-100" data-testid={`${testPrefix}-bad-date-row-${r.doc}-${r.item || i}`}>
            <td className="py-1.5 pr-3 font-mono">{r.doc}<span className="text-slate-400">/{r.fiscal_year}</span></td>
            <td className="py-1.5 pr-3">{r.doc_type}</td>
            <td className="py-1.5 pr-3 max-w-[220px] truncate" title={r.party_name}>{r.party_name || r.party_code}</td>
            <td className="py-1.5 pr-3 tabular-nums">{r.posting_date || "—"}</td>
            <td className="py-1.5 pr-3 tabular-nums font-semibold text-red-700">{r.invoice_date}</td>
            <td className="py-1.5 pr-3">{r.status}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{inr(r.amount)}</td>
            <td className="py-1.5 text-slate-600">{r.reason}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const BadDatesBanner = ({ side, testPrefix }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [dl, setDl] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(true);
  useEffect(() => {
    axios.get(`${API}/reports/${side}-ageing/bad-dates`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [side]);
  if (!data?.count) return null;

  const download = async () => {
    setDl(true);
    try {
      const r = await axios.get(`${API}/reports/${side}-ageing/bad-dates`, { params: { format: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${side}-bad-document-dates.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the list"); } finally { setDl(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${testPrefix}-bad-dates-banner`}
        className="w-full text-left rounded-lg border border-red-200 bg-red-50/60 px-4 py-2.5 text-xs text-red-800 hover:bg-red-50 transition-colors flex items-center gap-2">
        <CalendarX2 className="h-4 w-4 shrink-0" />
        <span>{data.open_count > 0
          ? <><b data-testid={`${testPrefix}-bad-dates-count`}>{data.open_count}</b> open bill line(s) worth <b>{inr(data.open_amount)}</b> have an impossible document date</>
          : <><b data-testid={`${testPrefix}-bad-dates-count`}>{data.count}</b> settled bill line(s) have an impossible document date</>}
          {" "}(in the future, or more than {data.max_gap_days} days from the posting date){data.open_count > 0 && data.count > data.open_count ? `, plus ${data.count - data.open_count} already settled` : ""}. Click to see them and correct in SAP.</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid={`${testPrefix}-bad-dates-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>Bills with impossible document dates</span>
              <button type="button" onClick={download} disabled={dl} data-testid={`${testPrefix}-bad-dates-download`}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                {dl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Excel
              </button>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">{data.note} Ageing uses the document date, so these bills land in the wrong bucket (or drop out of a back-dated ageing) until fixed in SAP.</p>
          <div className="flex items-center gap-1 text-xs" data-testid={`${testPrefix}-bad-dates-filter`}>
            {[[true, `Open (${data.open_count})`], [false, `All (${data.count})`]].map(([v, l]) => (
              <button key={l} type="button" onClick={() => setOnlyOpen(v)} data-testid={`${testPrefix}-bad-dates-filter-${v ? "open" : "all"}`}
                className={`rounded-full px-3 py-1 border ${onlyOpen === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <BadDatesTable rows={onlyOpen && data.open_count ? data.rows.filter((r) => r.status === "Open") : data.rows} testPrefix={testPrefix} />
        </DialogContent>
      </Dialog>
    </>
  );
};
